import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Send, X, Crown } from "lucide-react";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  let id = localStorage.getItem("ei-chat-session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("ei-chat-session", id);
  }
  return id;
};

const RoyalAssistant = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    if (!open || messages.length > 0) return;
    fetch(`${API}/chat/history/${sessionId.current}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.messages?.length) setMessages(d.messages);
        else setMessages([{ role: "assistant", content: t.chat.greeting }]);
      })
      .catch(() => setMessages([{ role: "assistant", content: t.chat.greeting }]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: msg }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") break;
          try {
            const j = JSON.parse(payload);
            if (j.delta) {
              setMessages((m) => {
                const c = [...m];
                c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + j.delta };
                return c;
              });
            }
            if (j.error) {
              setMessages((m) => {
                const c = [...m];
                c[c.length - 1] = { role: "assistant", content: j.error };
                return c;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = { role: "assistant", content: t.chat.error };
        return c;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-3">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            className="glass-card w-[92vw] max-w-sm h-[540px] max-h-[70vh] rounded-2xl border border-gold/25 shadow-[0_24px_70px_rgba(0,0,0,0.65)] flex flex-col overflow-hidden"
            data-testid="royal-assistant-panel"
          >
            <div className="flex items-center gap-3 border-b gold-hairline px-5 py-4 bg-[#0B0C10]/80">
              <img src="/assets/logo.jpg" alt="" className="h-10 w-10 rounded-full object-cover ring-1 ring-gold/50" />
              <div className="flex-1">
                <p className="font-display text-lg text-slate-100 flex items-center gap-2">
                  {t.chat.title}
                  <Crown size={14} className="text-gold" />
                </p>
                <p className="text-[11px] text-slate-500">{t.chat.subtitle}</p>
              </div>
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-gradient-to-r from-gold to-gold-amber text-obsidian font-medium rounded-br-md"
                        : "bg-[#12131A] border border-gold/15 text-slate-200 rounded-bl-md"
                    }`}
                  >
                    {m.content || (streaming && i === messages.length - 1 ? <span className="inline-block h-4 w-2 animate-pulse bg-gold/70 rounded" /> : null)}
                  </div>
                </div>
              ))}
            </div>

            {messages.length <= 1 && (
              <div className="px-4 pb-2 flex flex-wrap gap-2" data-testid="chat-quick-prompts">
                {t.chat.quick.map((q) => (
                  <button
                    key={q}
                    data-testid={`chat-quick-${q.slice(0, 12).replace(/\W/g, "-")}`}
                    onClick={() => send(q)}
                    className="rounded-full border border-gold/30 px-3 py-1.5 text-xs text-gold-light hover:bg-gold/10 transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex items-center gap-2 border-t gold-hairline px-4 py-3 bg-[#0B0C10]/80"
            >
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t.chat.placeholder}
                className="flex-1 bg-transparent text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none"
              />
              <button
                type="submit"
                disabled={streaming || !input.trim()}
                data-testid="chat-send-btn"
                className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-r from-gold to-gold-amber text-obsidian disabled:opacity-50 hover:shadow-[0_0_18px_rgba(212,175,55,0.5)] transition-shadow"
              >
                <Send size={15} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        data-testid="royal-assistant-trigger-button"
        onClick={() => setOpen(!open)}
        aria-label="Royal Assistant"
        className="relative flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-gold to-gold-amber text-obsidian shadow-[0_10px_32px_rgba(212,175,55,0.45)] hover:scale-110 transition-transform"
      >
        {open ? <X size={22} /> : <Crown size={24} />}
        {!open && <span className="absolute -top-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-emerald-400 ring-2 ring-[#0B0C10]" />}
      </button>
    </div>
  );
};

export default RoyalAssistant;
